import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, isBefore, addDays, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Activity, Syringe, Camera, MapPin, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useWeightRecords } from '@/hooks/useWeightRecords';
import { useVaccinations } from '@/hooks/useVaccinations';
import { usePetPosts } from '@/hooks/usePosts';
import { formatWeight } from '@/utils/weightFormatting';

interface PetDashboardProps {
  petId: string;
  petName: string;
}

const PetDashboard = ({ petId, petName }: PetDashboardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { records: weightRecords, isLoading: weightLoading } = useWeightRecords(petId) as any;
  const { vaccinations, isLoading: vacLoading } = useVaccinations(petId);
  const { data: posts, isLoading: postsLoading } = usePetPosts(petId);

  const chartData = useMemo(() => {
    if (!weightRecords) return [];
    return [...weightRecords]
      .sort(
        (a: any, b: any) =>
          new Date(a.measurement_date).getTime() - new Date(b.measurement_date).getTime()
      )
      .map((r: any) => ({
        date: format(new Date(r.measurement_date), 'MMM d'),
        weight: Number(r.weight_kg),
      }));
  }, [weightRecords]);

  const upcomingVaccinations = useMemo(() => {
    if (!vaccinations) return [];
    const now = new Date();
    const horizon = addDays(now, 30);
    return vaccinations
      .filter((v) => v.date_due && !v.date_given)
      .filter((v) => {
        const due = parseISO(v.date_due);
        return isAfter(due, addDays(now, -1)) && isBefore(due, horizon);
      })
      .sort((a, b) => a.date_due.localeCompare(b.date_due))
      .slice(0, 5);
  }, [vaccinations]);

  const recentPosts = (posts || []).slice(0, 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Health Tracking */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-primary" />
            {t('petDashboard.healthTracking', 'Health Tracking')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/medical/${petId}`)}
          >
            {t('petDashboard.viewAll', 'View all')}
          </Button>
        </CardHeader>
        <CardContent>
          {weightLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : chartData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t('petDashboard.noWeightData', 'No weight records yet.')}
            </div>
          ) : (
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(v: any) => formatWeight(Number(v))}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="weight"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Vaccinations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Syringe className="h-4 w-4 text-primary" />
            {t('petDashboard.upcomingVaccinations', 'Upcoming Vaccinations')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/medical/${petId}`)}
          >
            {t('petDashboard.viewAll', 'View all')}
          </Button>
        </CardHeader>
        <CardContent>
          {vacLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : upcomingVaccinations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              {t('petDashboard.noUpcomingVaccinations', 'No vaccinations due in the next 30 days.')}
            </p>
          ) : (
            <ul className="space-y-2">
              {upcomingVaccinations.map((v) => (
                <li
                  key={v.id}
                  className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0"
                >
                  <span className="text-sm font-medium">{v.vaccine_name}</span>
                  <Badge variant="outline">{format(parseISO(v.date_due), 'MMM d, yyyy')}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Daily Activity (placeholder) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            {t('petDashboard.dailyActivity', 'Daily Activity')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <div className="relative h-32 w-32">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="10"
              />
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="10"
                strokeDasharray={`${0.65 * 2 * Math.PI * 42} ${2 * Math.PI * 42}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold">65%</span>
              <span className="text-xs text-muted-foreground">
                {t('petDashboard.goal', 'of goal')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Memories */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4 text-primary" />
            {t('petDashboard.recentMemories', 'Recent Memories')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {postsLoading ? (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-md" />
              ))}
            </div>
          ) : recentPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {t('petDashboard.noMemories', "No memories yet.")}
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {recentPosts.map((post: any) => {
                const media = post.media_urls?.[0] || post.image_url;
                return (
                  <button
                    key={post.id}
                    onClick={() => navigate(`/post/${post.id}`)}
                    className="aspect-square rounded-md overflow-hidden bg-muted relative group"
                  >
                    {media ? (
                      <img
                        src={media}
                        alt={petName}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nearby Services */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-primary" />
            {t('petDashboard.nearbyServices', 'Nearby Pet Services')}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/services')}>
            {t('petDashboard.explore', 'Explore')}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t(
              'petDashboard.nearbyServicesHint',
              'Discover groomers, vets, walkers and pet-friendly places near you.'
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PetDashboard;
